// UserSearch Component - Search for other users

import React, { useState, useEffect, useCallback } from 'react';
import { Search, Users, ArrowLeft } from 'lucide-react';
import { PublicUserProfile } from '../../types';
import { searchUsers, getSearchableUsers } from '../../services/userSearchService';
import { UserCard } from './UserCard';
import { EmptyState } from '../common/EmptyState';

interface UserSearchProps {
  onBack: () => void;
  onSelectUser: (userId: string) => void;
}

export function UserSearch({ onBack, onSelectUser }: UserSearchProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<PublicUserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  // Load initial users
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const searchableUsers = await getSearchableUsers(30);
        setUsers(searchableUsers);
      } catch (error) {
        console.error('Error loading users:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  // Debounced search
  const handleSearch = useCallback(async (term: string) => {
    if (term.length < 2) {
      // Reset to all searchable users
      setSearching(true);
      try {
        const searchableUsers = await getSearchableUsers(30);
        setUsers(searchableUsers);
      } catch (error) {
        console.error('Error loading users:', error);
      } finally {
        setSearching(false);
      }
      return;
    }

    setSearching(true);
    try {
      const results = await searchUsers(term);
      setUsers(results);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounce effect
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, handleSearch]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">Community</h1>
              <p className="text-sm text-gray-500">Entdecke andere GoodPlace Nutzer</p>
            </div>
          </div>

          {/* Search Input */}
          <div className="mt-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Nach Namen suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <Users className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'Keine Nutzer gefunden' : 'Noch keine Community-Mitglieder'}
            </h3>
            <p className="text-gray-500">
              {searchTerm
                ? 'Versuche einen anderen Suchbegriff.'
                : 'Sei der Erste, der sein Profil sichtbar macht!'}
            </p>
          </div>
        ) : (
          <>
            {/* Results count */}
            <div className="mb-4 text-sm text-gray-500">
              {searching ? (
                'Suche...'
              ) : (
                `${users.length} Nutzer${users.length === 1 ? '' : ''} gefunden`
              )}
            </div>

            {/* User list */}
            <div className="space-y-3">
              {users.map((user) => (
                <UserCard
                  key={user.id}
                  user={user}
                  onClick={onSelectUser}
                />
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default UserSearch;
